import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { adminApi, Badge, Table, Toolbar, inpCls, btnGhost, btnDanger } from './_shared';
import { Search, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState('');
  const [archived, setArchived] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (archived) params.archived = archived;
      const r = await adminApi.projects(params);
      setProjects(r.projects || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, archived]);

  const toggleArchive = async (p) => {
    try { await adminApi.updateProject(p.project_id, { isArchived: !p.isArchived }); toast.success(p.isArchived ? 'Restored' : 'Archived'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const del = async (p) => {
    if (!window.confirm(`Soft-delete project "${p.projectName}"? It will be archived and marked DELETED.`)) return;
    try { await adminApi.deleteProject(p.project_id); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const cols = [
    { key: 'project', header: 'Project', render: p => (
      <div>
        <div className="text-slate-100">{p.projectName}</div>
        <div className="text-[11px] text-slate-500 font-mono">{p.projectCode}</div>
      </div>
    )},
    { key: 'client', header: 'Client', render: p => {
      const c = typeof p.client === 'object' ? p.client?.clientName : p.client;
      return <span className="text-slate-400">{c || '—'}</span>;
    }},
    { key: 'sector', header: 'Sector', render: p => (
      <span className="text-slate-400 text-xs">{(p.classification?.industrySector || p.sector || '—').replace(/_/g, ' ')}</span>
    )},
    { key: 'members', header: 'Members', render: p => <span className="text-slate-300 tabular-nums">{p.memberCount || 0}</span> },
    { key: 'dateFormat', header: 'Date Format', render: p => (
      <select
        className={`${inpCls} text-xs py-1`}
        value={p.dateFormat || 'DD-MM'}
        onChange={async (e) => {
          const val = e.target.value;
          try { await adminApi.updateProject(p.project_id, { dateFormat: val }); toast.success(`Date format set to ${val}`); load(); }
          catch (err) { toast.error(err.response?.data?.detail || 'Failed to set date format'); }
        }}
        data-testid={`date-fmt-${p.project_id}`}
      >
        <option value="DD-MM">DD-MM (European)</option>
        <option value="MM-DD">MM-DD (US)</option>
        <option value="ISO">ISO YYYY-MM-DD</option>
      </select>
    )},
    { key: 'status', header: 'Status', render: p => (
      <div className="flex flex-wrap gap-1">
        <Badge tone={p.isArchived ? 'amber' : 'green'}>{p.isArchived ? 'Archived' : 'Active'}</Badge>
        {p.projectStatus === 'DELETED' && <Badge tone="red">DELETED</Badge>}
      </div>
    )},
    { key: 'actions', header: '', render: p => (
      <div className="flex items-center gap-1 justify-end">
        <button onClick={() => toggleArchive(p)} className={btnGhost} data-testid={`arch-${p.project_id}`}>
          {p.isArchived ? <><ArchiveRestore className="w-3.5 h-3.5"/>Restore</> : <><Archive className="w-3.5 h-3.5"/>Archive</>}
        </button>
        <button onClick={() => del(p)} className={btnDanger} data-testid={`del-${p.project_id}`}><Trash2 className="w-3.5 h-3.5"/>Delete</button>
      </div>
    )},
  ];

  return (
    <AdminLayout title="Projects" subtitle="Admin · Global Directory">
      <Toolbar>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"/>
          <input className={`${inpCls} pl-9 w-full`} placeholder="Search by code, name, client…" value={q} onChange={e => setQ(e.target.value)} data-testid="projects-search"/>
        </div>
        <select className={inpCls} value={archived} onChange={e => setArchived(e.target.value)}>
          <option value="">All projects</option>
          <option value="false">Active only</option>
          <option value="true">Archived only</option>
        </select>
      </Toolbar>
      <Table columns={cols} rows={projects} empty={loading ? 'Loading…' : 'No projects match your filters.'} getKey={p => p.project_id}/>
    </AdminLayout>
  );
}
